import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";

type CronAlert = {
  id: string;
  target: string;
  severity: string;
  message: string;
  attempts: number;
  last_error: string | null;
  created_at: string;
};

export function CronAlertsPanel() {
  const [alerts, setAlerts] = useState<CronAlert[]>([]);
  const [resolvingId, setResolvingId] = useState<string | null>(null);

  const load = async () => {
    const { data } = await supabase
      .from("cron_alerts")
      .select("id, target, severity, message, attempts, last_error, created_at")
      .is("resolved_at", null)
      .order("created_at", { ascending: false })
      .limit(20);
    setAlerts((data as CronAlert[]) ?? []);
  };

  useEffect(() => {
    load();
    const ch = supabase
      .channel(`cron_alerts_admin:${Math.random().toString(36).slice(2)}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "cron_alerts" }, load)
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, []);

  const resolve = async (id: string) => {
    setResolvingId(id);
    const { data: { user } } = await supabase.auth.getUser();
    const { error } = await supabase
      .from("cron_alerts")
      .update({ resolved_at: new Date().toISOString(), resolved_by: user?.id ?? null })
      .eq("id", id);
    setResolvingId(null);
    if (error) return toast.error(error.message);
    setAlerts((prev) => prev.filter((a) => a.id !== id));
    toast.success("Alert resolved");
  };

  if (alerts.length === 0) return null;

  return (
    <div className="space-y-2">
      {alerts.map((a) => (
        <div
          key={a.id}
          className={`rounded-xl border p-4 flex items-start gap-3 ${
            a.severity === "critical"
              ? "border-destructive/60 bg-destructive/10"
              : "border-destructive/30 bg-destructive/5"
          }`}
        >
          <AlertTriangle className="size-4 text-destructive mt-0.5 shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono text-[10px] uppercase rounded px-1.5 py-0.5 border border-destructive/40 text-destructive">
                {a.severity} · {a.target}
              </span>
              <span className="font-mono text-[10px] text-muted-foreground">
                {a.attempts} attempts · {new Date(a.created_at).toLocaleString()}
              </span>
            </div>
            <p className="text-sm font-medium mt-1">{a.message}</p>
            {a.last_error && (
              <pre className="mt-2 text-xs text-muted-foreground bg-background/60 rounded p-2 overflow-x-auto whitespace-pre-wrap break-words">
                {a.last_error}
              </pre>
            )}
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => resolve(a.id)}
            disabled={resolvingId === a.id}
          >
            {resolvingId === a.id ? <Loader2 className="size-4 animate-spin" /> : <Check className="size-4" />}
            Resolve
          </Button>
        </div>
      ))}
    </div>
  );
}
